import React, { useEffect } from 'react';
import { useLocale } from '../../../../../i18n';
import {
  VStack,
  Grid,
  Button,
  Icon,
  Text,
  HStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../../../common/src/components/LoadingSpinner';
import AdvisoryAppShell from '../../../../flows/src/components/ui/advisory-flow/components/app-shell/AdvisoryAppShell';
import {
  GENERAL_PAGE_PX,
  GENERAL_PAGE_PY,
} from '../../../../common/src/components/GeneralPageLayout';
import FinalizePartyHatIllustration from '../../features/finalize/FinalizePartyHatIllustration';
import FinalizeTextBlock from '../../features/finalize/FinalizeTextBlock';
import { usePostPartyOffer } from '../../../../parties/src/api/postOffer';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';

interface Props {
  partyId: string;
  quoteId: string;
  contractStartDate: string;
}

const CyclingVlaanderenSign: React.FC<Props> = ({
  quoteId,
  partyId,
  contractStartDate,
}) => {
  const { userLang } = useLocale();
  const { distribution, user } = useAuth();
  const { t } = useTranslation();
  const {
    data,
    mutate,
    isLoading: isCreatingOffer,
    isError,
  } = usePostPartyOffer();

  const createOffer = () => {
    if (partyId) {
      mutate({
        partyId,
        payload: {
          quote_id: quoteId,
          start_date: contractStartDate,
          generate_sepa_document: true,
          language: user?.language || 'en',
          signature_redirect_url: `${window.location.origin}/thank-you-for-signing?language=${userLang}`,
        },
      });
    }
  };

  useEffect(() => {
    createOffer();
  }, []);

  const signUrl = `${process.env.REACT_APP_API_HOST}v1/api/broker/v1/api/distributions/${distribution.id}/offers/${data?.id}/revisions/${data?.revision}/signatures`;

  if (isCreatingOffer) return <LoadingSpinner />;

  return (
    <AdvisoryAppShell>
      <Grid
        gridTemplateColumns="auto 1fr"
        maxW="6xl"
        margin="0 auto"
        py={GENERAL_PAGE_PY}
        px={GENERAL_PAGE_PX}
        gap="6"
        h="100%"
        overflowY="auto"
      >
        {isError ? <div /> : <FinalizePartyHatIllustration />}
        <VStack spacing="14" align="flex-start" mt="10">
          <VStack align="stretch" spacing="3">
            <FinalizeTextBlock
              title={t('SALES-CONVERSATION_FINALIZE_BIKMO_TITLE')}
            >
              <Text>{t('SALES-CONVERSATION_FINALIZE_BIKMO_DESCRIPTION')}</Text>
            </FinalizeTextBlock>
            {isError && (
              <HStack>
                <Icon name="GeneralStatusWarningTriangle" color="danger.main" />
                <Text color="danger.main">{t('Something went wrong')}</Text>
              </HStack>
            )}
          </VStack>
          <Button
            isDisabled={isError}
            as="a"
            borderRadius="full"
            size="lg"
            leftIcon={<Icon name="GeneralActionsEditDocument" />}
            href={isError ? undefined : signUrl}
            target="_blank"
          >
            {t('Sign offer')}
          </Button>
        </VStack>
      </Grid>
    </AdvisoryAppShell>
  );
};

export default CyclingVlaanderenSign;
